import {
  AuthUser,
  SignInOutput,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  signIn as signInCognito,
  FetchUserAttributesOutput,
  signOut as signOutCognito,
  confirmSignIn as confirmSignInCognito,
} from "@aws-amplify/auth";

import { ServiceResponse } from "../types";

// Type for AWS Cognito errors
type CognitoError = Error & {
  name?: string;
  message?: string;
  code?: string;
  __type?: string;
};

export type CognitoUser = Pick<AuthUser, "username"> &
  FetchUserAttributesOutput;

const checkLoggedIn = async (): Promise<boolean> => {
  try {
    const session = await fetchAuthSession();
    return !!session.userSub;
  } catch (e) {
    console.error("checkLoggedIn", e);
    return false;
  }
};
const getUser = async (): Promise<CognitoUser | undefined> => {
  try {
    const cognitoUser = await getCurrentUser();
    const attr = await fetchUserAttributes();
    if (cognitoUser && attr) {
      return {
        ...{
          username: cognitoUser.username,
        },
        ...attr,
      };
    }
  } catch (e) {
    console.error("getUser", e);
  }
  return undefined;
};
const signIn = async (
  username: string
): Promise<ServiceResponse<SignInOutput>> => {
  try {
    const signInCognitoResponse = await signInCognito({
      username,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
      },
    });
    return {
      success: true,
      data: signInCognitoResponse,
    };
  } catch (err: unknown) {
    const e = err as CognitoError;
    // Detailed logging for debugging
    console.error("cognito signIn error:", {
      name: e?.name,
      message: e?.message,
      code: e?.code,
      __type: e?.__type,
      stack: e?.stack,
      username, // Safe to log username for debugging
      timestamp: new Date().toISOString(),
    });

    // Map error types to codes for user-facing messages
    let errorCode = "1"; // Default/unknown error

    if (e?.name === "NotAuthorizedException") {
      errorCode = "2"; // Invalid credentials
    } else if (e?.name === "UserNotFoundException") {
      errorCode = "3"; // User doesn't exist
    } else if (e?.name === "UserNotConfirmedException") {
      errorCode = "4"; // User not confirmed
    } else if (e?.name === "PasswordResetRequiredException") {
      errorCode = "5"; // Password reset required
    } else if (e?.name === "InvalidParameterException") {
      errorCode = "6"; // Invalid parameters
    } else if (e?.name === "NetworkError" || e?.message?.includes("Network")) {
      errorCode = "7"; // Network issues
    } else if (
      e?.name === "ConfigurationError" ||
      e?.message?.includes("Configuration")
    ) {
      errorCode = "8"; // Configuration issues
    }

    return {
      success: false,
      errors: [
        {
          code: "Error",
          message: `Could not sign in to Cognito. [${errorCode}]`,
        },
      ],
    };
  }
};
const signOut = async () => {
  try {
    await signOutCognito();
  } catch (e) {
    console.error("signOut", e);
  }
};
const confirmSignIn = async (
  challengeResponse: string
): Promise<ServiceResponse<boolean>> => {
  try {
    const res = await confirmSignInCognito({
      challengeResponse,
    });
    if (res?.isSignedIn) {
      return { success: true, data: true };
    }
    return {
      success: false,
      errors: [{ code: "Error", message: "Code failed." }],
    };
  } catch (e) {
    const name = (e as any)?.name;
    const message = (e as any)?.message;
    console.error("confirmSignIn", name, message);
    return {
      success: false,
      errors: [{ code: name, message: `Could not confirm sign in. [${name}]` }],
    };
  }
};
const getToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (e) {
    console.error("getToken", e);
    return null;
  }
};
const CognitoService = {
  getUser,
  signIn,
  signOut,
  confirmSignIn,
  getToken,
  checkLoggedIn,
};
export default CognitoService;
